import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CAMERA_CATEGORY_COLORS,
  CAMERA_OVERLAY_COLOR,
  EMPTY_CAMERA_COLLECTION,
  cameraCategoryLabel,
  getCameraOverlayRefreshMs,
  normalizeCameraCollection,
  type CameraFeatureCollection,
} from '../services/cameraOverlayService';
import { fetchCameraOverlaysGeoJson } from '../services/cameraOverlayApi';

/** Facet key used for features whose source/category is missing. */
export const CAMERA_FACET_UNKNOWN = '__unknown__';

const HIDDEN_STORAGE_KEY = 'pathfinder.cameraOverlay.hidden.v1';
const DISPLAY_STORAGE_KEY = 'pathfinder.cameraOverlay.display.v1';

export type CameraMarkerSize = 'small' | 'medium' | 'large';

/** How camera markers are drawn (shared by Pathfinder + COP). */
export type CameraLayerDisplay = {
  /** Group nearby cameras into count bubbles until zoomed in. */
  cluster: boolean;
  /** Show camera names under unclustered markers. */
  labels: boolean;
  /** Marker radius preset. */
  size: CameraMarkerSize;
};

export const DEFAULT_CAMERA_DISPLAY: CameraLayerDisplay = { cluster: true, labels: false, size: 'medium' };

function loadDisplay(): CameraLayerDisplay {
  if (typeof window === 'undefined') return DEFAULT_CAMERA_DISPLAY;
  try {
    const raw = window.localStorage.getItem(DISPLAY_STORAGE_KEY);
    if (!raw) return DEFAULT_CAMERA_DISPLAY;
    const parsed = JSON.parse(raw) as Partial<CameraLayerDisplay>;
    return {
      cluster: typeof parsed.cluster === 'boolean' ? parsed.cluster : DEFAULT_CAMERA_DISPLAY.cluster,
      labels: typeof parsed.labels === 'boolean' ? parsed.labels : DEFAULT_CAMERA_DISPLAY.labels,
      size:
        parsed.size === 'small' || parsed.size === 'medium' || parsed.size === 'large'
          ? parsed.size
          : DEFAULT_CAMERA_DISPLAY.size,
    };
  } catch {
    return DEFAULT_CAMERA_DISPLAY;
  }
}

function saveDisplay(display: CameraLayerDisplay): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISPLAY_STORAGE_KEY, JSON.stringify(display));
  } catch {
    /* ignore */
  }
}

export type CameraFacetItem = {
  /** Stable key (category slug / source name, or CAMERA_FACET_UNKNOWN). */
  key: string;
  /** Human-readable label. */
  label: string;
  /** Marker color (categories only; sources use the default overlay color). */
  color: string;
  /** Number of loaded cameras with this value. */
  count: number;
  /** False when the user has toggled this value off. */
  visible: boolean;
};

export type CameraLayerFilters = {
  hiddenCategories: string[];
  hiddenSources: string[];
};

type PersistedFilters = Partial<CameraLayerFilters>;

function loadHidden(): CameraLayerFilters {
  if (typeof window === 'undefined') return { hiddenCategories: [], hiddenSources: [] };
  try {
    const raw = window.localStorage.getItem(HIDDEN_STORAGE_KEY);
    if (!raw) return { hiddenCategories: [], hiddenSources: [] };
    const parsed = JSON.parse(raw) as PersistedFilters;
    return {
      hiddenCategories: Array.isArray(parsed.hiddenCategories)
        ? parsed.hiddenCategories.filter((v) => typeof v === 'string')
        : [],
      hiddenSources: Array.isArray(parsed.hiddenSources)
        ? parsed.hiddenSources.filter((v) => typeof v === 'string')
        : [],
    };
  } catch {
    return { hiddenCategories: [], hiddenSources: [] };
  }
}

function saveHidden(filters: CameraLayerFilters): void {
  if (typeof window === 'undefined') return;
  try {
    if (filters.hiddenCategories.length === 0 && filters.hiddenSources.length === 0) {
      window.localStorage.removeItem(HIDDEN_STORAGE_KEY);
    } else {
      window.localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify(filters));
    }
  } catch {
    /* storage unavailable (private mode / quota) — filters stay in memory */
  }
}

function toggleIn(list: string[], key: string, visible?: boolean): string[] {
  const hidden = list.includes(key);
  const shouldHide = visible === undefined ? !hidden : !visible;
  if (shouldHide && !hidden) return [...list, key];
  if (!shouldHide && hidden) return list.filter((k) => k !== key);
  return list;
}

function categoryKey(props: Record<string, unknown>): string {
  const raw = props.category;
  return typeof raw === 'string' && raw.trim() ? raw.trim().toLowerCase() : CAMERA_FACET_UNKNOWN;
}

function sourceKey(props: Record<string, unknown>): string {
  const raw = props.source;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : CAMERA_FACET_UNKNOWN;
}

/**
 * Shared admin-uploaded camera overlays for Pathfinder + COP.
 *
 * - `enabled` loads every active upload (map view is never changed here).
 * - Category / source toggles are applied client-side, so flipping a layer is
 *   instant and never re-hits the API. Hidden values persist in localStorage.
 * - New categories/sources that appear in later uploads default to visible.
 */
export function useCameraLocationsOverlay(enabled: boolean) {
  const [sharedData, setSharedData] =
    useState<CameraFeatureCollection>(EMPTY_CAMERA_COLLECTION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CameraLayerFilters>(loadHidden);
  const [display, setDisplayState] = useState<CameraLayerDisplay>(loadDisplay);
  const mountedRef = useRef(true);

  const refreshShared = useCallback(async () => {
    if (!enabled) return;
    try {
      const next = await fetchCameraOverlaysGeoJson();
      if (!mountedRef.current) return;
      const normalized = normalizeCameraCollection(
        (next?.type === 'FeatureCollection'
          ? next
          : { type: 'FeatureCollection', features: [] }) as GeoJSON.FeatureCollection
      );
      setSharedData(normalized);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to load shared camera overlays');
      setError(typeof detail === 'string' ? detail : 'Failed to load shared camera overlays');
      setSharedData(EMPTY_CAMERA_COLLECTION);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setSharedData(EMPTY_CAMERA_COLLECTION);
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        await refreshShared();
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
    const timer = window.setInterval(() => {
      void refreshShared();
    }, getCameraOverlayRefreshMs());
    return () => window.clearInterval(timer);
  }, [enabled, refreshShared]);

  useEffect(() => {
    saveHidden(filters);
  }, [filters]);

  useEffect(() => {
    saveDisplay(display);
  }, [display]);

  const setDisplay = useCallback((patch: Partial<CameraLayerDisplay>) => {
    setDisplayState((prev) => ({ ...prev, ...patch }));
  }, []);

  const allData = useMemo(() => (enabled ? sharedData : EMPTY_CAMERA_COLLECTION), [enabled, sharedData]);

  const hiddenCategorySet = useMemo(() => new Set(filters.hiddenCategories), [filters.hiddenCategories]);
  const hiddenSourceSet = useMemo(() => new Set(filters.hiddenSources), [filters.hiddenSources]);

  /** Features that pass the category + source toggles. */
  const data = useMemo<CameraFeatureCollection>(() => {
    if (hiddenCategorySet.size === 0 && hiddenSourceSet.size === 0) return allData;
    return {
      type: 'FeatureCollection',
      features: allData.features.filter((f) => {
        const props = (f.properties || {}) as Record<string, unknown>;
        return !hiddenCategorySet.has(categoryKey(props)) && !hiddenSourceSet.has(sourceKey(props));
      }),
    };
  }, [allData, hiddenCategorySet, hiddenSourceSet]);

  /** Category / source facets computed from everything loaded (not just visible). */
  const facets = useMemo(() => {
    const catCounts = new Map<string, number>();
    const srcCounts = new Map<string, number>();
    for (const f of allData.features) {
      const props = (f.properties || {}) as Record<string, unknown>;
      const c = categoryKey(props);
      const s = sourceKey(props);
      catCounts.set(c, (catCounts.get(c) || 0) + 1);
      srcCounts.set(s, (srcCounts.get(s) || 0) + 1);
    }
    const categories: CameraFacetItem[] = Array.from(catCounts.entries())
      .map(([key, count]) => ({
        key,
        label: key === CAMERA_FACET_UNKNOWN ? 'Uncategorized' : cameraCategoryLabel(key),
        color: CAMERA_CATEGORY_COLORS[key] || CAMERA_OVERLAY_COLOR,
        count,
        visible: !hiddenCategorySet.has(key),
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    const sources: CameraFacetItem[] = Array.from(srcCounts.entries())
      .map(([key, count]) => ({
        key,
        label: key === CAMERA_FACET_UNKNOWN ? 'Unknown source' : key,
        color: CAMERA_OVERLAY_COLOR,
        count,
        visible: !hiddenSourceSet.has(key),
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return { categories, sources };
  }, [allData, hiddenCategorySet, hiddenSourceSet]);

  const setCategoryVisible = useCallback((key: string, visible?: boolean) => {
    setFilters((prev) => ({ ...prev, hiddenCategories: toggleIn(prev.hiddenCategories, key, visible) }));
  }, []);

  const setSourceVisible = useCallback((key: string, visible?: boolean) => {
    setFilters((prev) => ({ ...prev, hiddenSources: toggleIn(prev.hiddenSources, key, visible) }));
  }, []);

  /** Show only this category (hide every other category currently loaded). */
  const soloCategory = useCallback(
    (key: string) => {
      setFilters((prev) => ({
        ...prev,
        hiddenCategories: facets.categories.map((c) => c.key).filter((k) => k !== key),
      }));
    },
    [facets.categories]
  );

  /** Show only this source. */
  const soloSource = useCallback(
    (key: string) => {
      setFilters((prev) => ({
        ...prev,
        hiddenSources: facets.sources.map((s) => s.key).filter((k) => k !== key),
      }));
    },
    [facets.sources]
  );

  const setAllCategoriesVisible = useCallback(
    (visible: boolean) => {
      setFilters((prev) => ({
        ...prev,
        hiddenCategories: visible ? [] : facets.categories.map((c) => c.key),
      }));
    },
    [facets.categories]
  );

  const setAllSourcesVisible = useCallback(
    (visible: boolean) => {
      setFilters((prev) => ({
        ...prev,
        hiddenSources: visible ? [] : facets.sources.map((s) => s.key),
      }));
    },
    [facets.sources]
  );

  const clearFilters = useCallback(() => {
    setFilters({ hiddenCategories: [], hiddenSources: [] });
  }, []);

  const hiddenCount = filters.hiddenCategories.length + filters.hiddenSources.length;

  return {
    /** Visible features (after category/source toggles). */
    data,
    /** Everything loaded, ignoring toggles. */
    allData,
    /** Visible camera count. */
    featureCount: data.features.length,
    /** Loaded camera count before filtering. */
    totalCount: allData.features.length,
    loading,
    error,
    refresh: refreshShared,
    facets,
    filters,
    /** Cluster / labels / marker size (persisted). */
    display,
    setDisplay,
    /** True when at least one category or source is toggled off. */
    hasActiveFilters: hiddenCount > 0,
    setCategoryVisible,
    setSourceVisible,
    soloCategory,
    soloSource,
    setAllCategoriesVisible,
    setAllSourcesVisible,
    clearFilters,
  };
}

export type CameraLocationsOverlay = ReturnType<typeof useCameraLocationsOverlay>;
